<template>
  <div
    class="selection-tab"
    :class="{
      'selection-tab--button': url,
      [$class('disabled')]: disabled || visuallyDisabled,
      [$class('loading')]: loading,
    }"
  >
    <a
      v-if="url"
      class=" selection-tab__label text-body-2xs-regular"
      :href="$localizeUrl(url)"
      :title="title"
      v-html="formattedLabel"
    />

    <template v-else>
      <input
        :id="id"
        v-model="localModelValue"
        class="selection-tab__input visually-hidden"
        :class="{
          [$class('disabled')]: disabled || visuallyDisabled,
          [$class('loading')]: loading,
        }"
        :disabled="disabled || loading"
        :name="name"
        type="radio"
        :value="value"
        :aria-selected="value === localModelValue"
        @change="updateVModel(value)"
      >

      <label
        class="selection-tab__label text-body-2xs-regular"
        :for="id"
        :title="title"
      >
        <span
          class="selection-tab__label-text"
          v-text="label"
        />

        <span
          v-if="preorder"
          class="selection-tab__status"
          v-html="preorderMessage"
        />

        <span
          v-else-if="lowStock"
          class="selection-tab__status selection-tab__status--low-stock"
          v-text="$string('product.form.low_stock')"
        />

        <span
          v-else-if="disabled || visuallyDisabled"
          class="selection-tab__status selection-tab__status--notify"
        >
          <icon-envelope />
          <span v-text="$string('product.cta.notify_me')" />
        </span>
      </label>
    </template>
  </div>
</template>

<script>

/**
 * Vue: Selection tab (selection-tab)
 * -----------------------------------------------------------------------------
 * Simple tab button with active, disabled, and initial states.
 *
 * @param {Boolean} [disabled] - Tab is disabled.
 * @param {String} [id] - ID for input, required for input.
 * @param {String} label - Tab label.
 * @param {Boolean} [loading] - Loading state.
 * @param {Number|String} modelValue - Current value provided by v-model. This
 * is required when using `input` element (when no `url` prop is passed).
 * @param {String} [name] - Radio input group name, required for input.
 * @param {Boolean} lowStock - Whether to show on tab when low stock.
 * @param {Boolean|String} [preorder] - Preorder date for this value.
 * @param {String} [title] - Title attribute.
 * @param {String} [url] - URL to navigate to when clicked, replaces input.
 * @param {Number|String} [value] - Radio input value, required for input.
 * @param {Boolean} [visuallyDisabled] - Tab is visually disabled but can still
 * be selected.
 *
 * @emits update:model-value - Emit v-model update event.
 *
 */
import IconEnvelope from '~icons/general/envelope.svg'

export default {
  name: 'SelectionTab',

  components: {
    IconEnvelope,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    lowStock: {
      type: Boolean,
      default: false,
    },
    modelValue: {
      type: [Number, String],
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    preorder: {
      type: [Boolean, String],
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    url: {
      type: String,
      default: '',
    },
    value: {
      type: [Number, String],
      default: '',
    },
    visuallyDisabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'update:model-value': () => true,
  },

  data() {
    return {
      localModelValue: '',
    }
  },

  computed: {

    /**
     * Formatted label with messaging added, if necessary.
     * @returns {String}
     */
    formattedLabel() {
      let label = this.label

      if (this.preorderMessage) {
        label += `<span>${this.preorderMessage}</span>`
      } else if (this.lowStock) {
        label += `<span>${this.$string('product.form.low_stock')}</span>`
      }

      return label
    },

    /**
     * Compute preorder message.
     * - If there should be one.
     * @returns {String}
     */
    preorderMessage() {
      if (!this.preorder) {
        return ''
      }

      const date = this.$formatPreorderDate({
        date: this.preorder,
        dateFormat: 'preorder_pdp_html',
      })
      return this.$string('product.preorder.options', { replace: { date } })
    },
  },

  watch: {

    /**
     * Watch v-model value to update local value.
     */
    modelValue() {
      this.localModelValue = this.modelValue
    },
  },

  mounted() {
    this.localModelValue = this.modelValue
  },

  methods: {

    /**
     * Emit update v-model event.
     * @param {Number|String} value - Update value.
     */
    updateVModel(value) {
      this.$emit('update:model-value', value)
    },
  },
}
</script>

<style lang="scss">
@import './selection-tab';
</style>
